import { zodResponseFormat } from 'openai/helpers/zod';
import { z } from 'zod';
import type { OpenAI } from 'openai';
import { ChatCompletionMessageParam } from 'openai/resources/chat/completions';
import { getOpenAIClient } from '@/singleton-clients';
import { DEFAULT_LLM } from '@/constants';

type StructuredCompletionParams<T> = {
	systemPrompt: string;
	userPrompt: string;
	responseFormat:
		| { type: 'json_schema'; schema: z.ZodSchema<T>; name: string }
		| { type: 'json_object' };
	model?: string;
	temperature?: number;
	sessionId?: string | null;
	openAIClient?: OpenAI;
};

/**
 * Creates a structured completion using OpenAI with consistent configuration
 * across all tools.
 */
export async function createStructuredCompletion<T>(
	params: StructuredCompletionParams<T>,
): Promise<T> {
	const {
		systemPrompt,
		userPrompt,
		responseFormat,
		model = DEFAULT_LLM,
		temperature = 0,
		sessionId,
		openAIClient = getOpenAIClient(),
	} = params;

	// Determine response format for OpenAI
	const openAiResponseFormat =
		responseFormat.type === 'json_schema'
			? zodResponseFormat(responseFormat.schema, responseFormat.name)
			: { type: 'json_object' as const };

	const response = await openAIClient.beta.chat.completions.parse({
		model,
		messages: [
			{ role: 'system', content: systemPrompt },
			{ role: 'user', content: userPrompt },
		],
		response_format: openAiResponseFormat,
		tools: [],
		tool_choice: 'none',
		temperature,
		metadata: sessionId ? { refSessionId: sessionId } : undefined,
	});

	// Handle response based on format type
	const message = response.choices[0].message;

	if (responseFormat.type === 'json_schema') {
		// Check if response comes as tool_calls (structured output sometimes returns this way)
		if (message.tool_calls && message.tool_calls.length > 0) {
			const toolCall = message.tool_calls[0];
			if (toolCall.function && toolCall.function.arguments) {
				const parsedArguments = JSON.parse(toolCall.function.arguments);
				return parsedArguments as T;
			}
		}

		// Fallback to parsed content (standard structured output)
		const parsedResponse = message.parsed as T;
		if (!parsedResponse) {
			throw new Error('No valid response from AI');
		}
		return parsedResponse;
	} else {
		// Handle JSON object responses
		const content = message.content || '{}';
		const parsedResponse = JSON.parse(content);
		if (!parsedResponse) {
			throw new Error('No valid response from AI');
		}
		return parsedResponse as T;
	}
}

type ToolsCompletionParams = {
	systemPrompt: string;
	userPrompt: string;
	tools: any[];
	model?: string;
	temperature?: number;
	toolChoice?:
		| 'auto'
		| 'none'
		| { type: 'function'; function: { name: string } };
	openAIClient?: OpenAI;
};

// Generic message type to avoid dependency on assistant's Message type
type GenericMessage = {
	role: string;
	content?: string | any;
	tool_call_id?: string;
	[key: string]: any;
};

type ToolsCompletionResult = {
	toolMessages: GenericMessage[];
	assistantMessages: string[];
	combinedMessage: string;
	isError: boolean;
};

/**
 * Creates a tools completion using OpenAI with streaming support.
 * Returns captured messages and a combined message string ready for use.
 */
export async function createToolsCompletion(
	params: ToolsCompletionParams,
): Promise<ToolsCompletionResult> {
	const {
		systemPrompt,
		userPrompt,
		tools,
		model = DEFAULT_LLM,
		temperature = 0,
		toolChoice = 'auto',
		openAIClient = getOpenAIClient(),
	} = params;

	return new Promise<ToolsCompletionResult>((resolve) => {
		const toolMessages: GenericMessage[] = [];
		const assistantMessages: string[] = [];

		openAIClient.beta.chat.completions
			.runTools({
				messages: [
					{
						role: 'system',
						content: systemPrompt,
					},
					{
						role: 'user',
						content: [
							{
								type: 'text',
								text: userPrompt,
							},
						],
					},
				],
				model,
				stream: true,
				temperature,
				tools,
				tool_choice: toolChoice,
			})
			.on('message', (message: ChatCompletionMessageParam) => {
				if (message.role === 'tool') {
					toolMessages.push(message as GenericMessage);
				} else if (message.role === 'assistant' && message.content) {
					assistantMessages.push(message.content as string);
				}
			})
			.on('end', () => {
				let isError = false;
				let combinedMessage = '';

				try {
					// Extract tool messages
					const toolMessagesText = toolMessages
						.map(
							(message) =>
								JSON.parse(message.content as string).message,
						)
						.join('\n');

					const assistantMessagesText = assistantMessages.join('\n');

					// Error Detection Logic:
					if (
						toolMessages.length === 0 &&
						assistantMessages.length > 0
					) {
						// LLM didn't execute tools but provided explanation
						isError = true;
						combinedMessage = assistantMessagesText; // Use LLM's explanation as error message
					} else if (
						toolMessages.length === 0 &&
						assistantMessages.length === 0
					) {
						// No tools and no explanation - something went wrong
						isError = true;
						combinedMessage =
							'No tools executed and no response from LLM';
					} else {
						// Tools were executed - combine normally
						combinedMessage = [
							toolMessagesText,
							assistantMessagesText,
						]
							.filter(Boolean)
							.join('\n');

						// Check if results are empty despite tool execution
						if (combinedMessage.trim() === '') {
							isError = true;
							combinedMessage =
								'Tools executed but returned empty results';
						}
					}
				} catch {
					isError = true;
					combinedMessage = 'Malformed tool response detected';
				}

				resolve({
					toolMessages,
					assistantMessages,
					combinedMessage,
					isError,
				});
			})
			.on('error', (err: Error) => {
				// Handle Error objects properly - JSON.stringify on Error returns {}
				const errorMessage =
					err instanceof Error
						? JSON.stringify(
								{
									name: err.name,
									message: err.message,
									stack: err.stack,
								},
								null,
								2,
						  )
						: JSON.stringify(err, null, 2);
				resolve({
					toolMessages: [],
					assistantMessages: [],
					combinedMessage: errorMessage,
					isError: true,
				});
			});
	});
}
